/* @refresh reload */
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { LanguageProvider } from './contexts/LanguageContext'
import { DialogProvider } from './contexts/DialogContext'
import { clearAuthSession } from './lib/utils'
import './index.css'

type AppRoot = ReturnType<typeof ReactDOM.createRoot>
type AppWindow = typeof window & {
  __YIJIANMAI_APP_ROOT__?: AppRoot
  __YIJIANMAI_ROOT_ELEMENT__?: HTMLElement
  __YIJIANMAI_ROOT_OBSERVER__?: MutationObserver
  __YIJIANMAI_ROOT_DEBUGGER_INSTALLED__?: boolean
  __YIJIANMAI_BOOT_COUNT__?: number
  __YIJIANMAI_FETCH_AUTH_GUARD_INSTALLED__?: boolean
  __YIJIANMAI_AUTH_REDIRECT_LOCK_UNTIL__?: number
}

const ROOT_MARKER = 'data-ozon-app-root'
const SHELL_MARKER = 'data-yijianmai-app-shell'
const THEME_STORAGE_KEY = 'ui:theme'
const LEGACY_THEME_STORAGE_KEY = 'theme'
const AUTH_REDIRECT_THROTTLE_MS = 2000
const AUTH_ERROR_CODES = new Set(['UNAUTHORIZED', 'TOKEN_EXPIRED', 'TOKEN_REVOKED', 'INVALID_TOKEN', 'USER_NOT_FOUND'])
const AUTH_IGNORE_PATH_PREFIXES = ['/api/v1/auth/login', '/api/v1/auth/sms', '/api/v1/auth/turnstile', '/api/v1/auth/password']

type RootErrorBoundaryState = {
  hasError: boolean
  error?: Error | null
}

class RootErrorBoundary extends React.Component<{ children: React.ReactNode }, RootErrorBoundaryState> {
  state: RootErrorBoundaryState = {
    hasError: false,
    error: null,
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[RootErrorBoundary]', error, errorInfo)
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const title = '页面发生错误'
    const subtitle = '你可以刷新页面或返回任务中心继续。'
    const errorName = this.state.error?.name || 'Error'
    const errorMessage = this.state.error?.message || 'Unknown error'

    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)]">
          <div className="text-sm font-black text-slate-900">{title}</div>
          <div className="mt-1 text-xs text-slate-500">{subtitle}</div>

          <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="text-xs font-bold text-slate-800">{errorName}</div>
            <pre className="mt-3 whitespace-pre-wrap break-words text-[11px] text-slate-600 leading-relaxed">{errorMessage}</pre>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                window.location.href = '/task-center'
              }}
              className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700"
            >
              返回任务中心
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = '/workspace'
              }}
              className="rounded-xl bg-white px-4 py-2 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
            >
              返回工作台
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl bg-white px-4 py-2 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
            >
              刷新
            </button>
          </div>
        </div>
      </div>
    )
  }
}

const applyThemePreference = () => {
  if (typeof document === 'undefined') {
    return
  }
  try {
    const stored = window.localStorage?.getItem(THEME_STORAGE_KEY) || window.localStorage?.getItem(LEGACY_THEME_STORAGE_KEY)
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches
    const shouldUseDark = stored ? stored === 'dark' : Boolean(prefersDark)
    document.documentElement.classList.toggle('dark', shouldUseDark)
  } catch (error) {
    // ignore theme errors
  }
}

const resolveRequestUrl = (input: RequestInfo | URL) => {
  if (typeof input === 'string') {
    return input
  }
  if (input instanceof URL) {
    return input.toString()
  }
  return input.url
}

const resolveAuthorizationHeader = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.headers) {
    return new Headers(init.headers).get('Authorization')
  }
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.headers.get('Authorization')
  }
  return null
}

const extractAuthErrorCode = async (response: Response) => {
  const payload = await response.clone().json().catch(() => null)
  if (!payload || typeof payload !== 'object') {
    return ''
  }
  const maybeCode = (payload as { code?: unknown }).code
  if (typeof maybeCode !== 'string') {
    return ''
  }
  return maybeCode.trim().toUpperCase()
}

const shouldSkipUnauthorizedRedirect = (requestUrl: string) => {
  try {
    const url = new URL(requestUrl, window.location.origin)
    return AUTH_IGNORE_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))
  } catch {
    return AUTH_IGNORE_PATH_PREFIXES.some((prefix) => requestUrl.includes(prefix))
  }
}

const maybeTriggerAuthExpiredRedirect = async (
  appWindow: AppWindow,
  requestUrl: string,
  response: Response,
  hadBearerToken: boolean
) => {
  if (!hadBearerToken || response.status !== 401) {
    return
  }
  if (shouldSkipUnauthorizedRedirect(requestUrl)) {
    return
  }
  const code = await extractAuthErrorCode(response)
  if (code && !AUTH_ERROR_CODES.has(code)) {
    return
  }

  const now = Date.now()
  const lockUntil = appWindow.__YIJIANMAI_AUTH_REDIRECT_LOCK_UNTIL__ ?? 0
  if (lockUntil > now) {
    return
  }
  appWindow.__YIJIANMAI_AUTH_REDIRECT_LOCK_UNTIL__ = now + AUTH_REDIRECT_THROTTLE_MS

  clearAuthSession()
  window.dispatchEvent(
    new CustomEvent('auth:unauthorized', {
      detail: { code: code || 'UNAUTHORIZED', status: response.status, path: requestUrl },
    })
  )
}

const installUnauthorizedResponseGuard = (appWindow: AppWindow) => {
  if (appWindow.__YIJIANMAI_FETCH_AUTH_GUARD_INSTALLED__) {
    return
  }
  if (typeof window.fetch !== 'function') {
    return
  }
  appWindow.__YIJIANMAI_FETCH_AUTH_GUARD_INSTALLED__ = true
  const nativeFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl = resolveRequestUrl(input)
    const authorization = resolveAuthorizationHeader(input, init)
    const hadBearerToken = typeof authorization === 'string' && authorization.toLowerCase().startsWith('bearer ')
    const response = await nativeFetch(input, init)
    void maybeTriggerAuthExpiredRedirect(appWindow, requestUrl, response, hadBearerToken)
    return response
  }
}

const isRootDebugEnabled = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.has('debugRoot')) {
      return true
    }
    return window.localStorage?.getItem('debug:root') === '1'
  } catch (error) {
    return false
  }
}

const nodeContainsRoot = (node: Node) => {
  if (!(node instanceof Element)) {
    return false
  }
  if (node.id === 'root') {
    return true
  }
  if (node.getAttribute(ROOT_MARKER) === 'true') {
    return true
  }
  if (node.getAttribute(SHELL_MARKER) === 'true') {
    return true
  }
  if (node.childElementCount === 0) {
    return false
  }
  return node.querySelector(`#root, [${ROOT_MARKER}="true"], [${SHELL_MARKER}="true"]`) !== null
}

const nodeIntroducesDuplicateRoot = (node: Element) => {
  const introducesShell =
    node.getAttribute(SHELL_MARKER) === 'true' || node.querySelector(`[${SHELL_MARKER}="true"]`) !== null
  if (introducesShell) {
    const existingShells = document.querySelectorAll(`[${SHELL_MARKER}="true"]`).length
    if (existingShells > 0) {
      return true
    }
  }

  const introducesRoot = node.id === 'root' || node.querySelector('#root') !== null
  if (introducesRoot) {
    const existingRoots = document.querySelectorAll('#root').length
    if (existingRoots > 0) {
      return true
    }
  }

  return false
}

const installRootInjectionDebugger = (appWindow: AppWindow) => {
  if (!isRootDebugEnabled()) {
    return
  }
  if (appWindow.__YIJIANMAI_ROOT_DEBUGGER_INSTALLED__) {
    return
  }
  appWindow.__YIJIANMAI_ROOT_DEBUGGER_INSTALLED__ = true

  const originalAppendChild = Node.prototype.appendChild
  const originalInsertBefore = Node.prototype.insertBefore
  const originalReplaceChild = Node.prototype.replaceChild
  const nodeProto = Node.prototype as any

  const logIfNeeded = (node: Node) => {
    if (!nodeContainsRoot(node)) {
      return
    }
    if (!(node instanceof Element)) {
      return
    }
    if (!nodeIntroducesDuplicateRoot(node)) {
      return
    }
    console.error('[RootDebug] 检测到重复注入（新增了 #root 或 app-shell）', node, new Error().stack)
  }

  nodeProto.appendChild = function appendChildPatched(this: Node, newChild: Node) {
    logIfNeeded(newChild)
    return originalAppendChild.call(this, newChild)
  }

  nodeProto.insertBefore = function insertBeforePatched(this: Node, newChild: Node, refChild: Node | null) {
    logIfNeeded(newChild)
    return originalInsertBefore.call(this, newChild, refChild)
  }

  nodeProto.replaceChild = function replaceChildPatched(this: Node, newChild: Node, oldChild: Node) {
    logIfNeeded(newChild)
    return originalReplaceChild.call(this, newChild, oldChild)
  }
}

const pickRootElement = () => {
  const candidates = Array.from(document.querySelectorAll('#root')) as HTMLElement[]
  if (candidates.length === 0) {
    return null
  }
  const marked = candidates.find((node) => node.getAttribute(ROOT_MARKER) === 'true')
  return marked ?? candidates[0]
}

const cleanupDuplicateRoots = (primaryRoot: HTMLElement) => {
  const roots = Array.from(document.querySelectorAll('#root')) as HTMLElement[]
  roots.forEach((node) => {
    if (node !== primaryRoot) {
      node.remove()
    }
  })
}

const cleanupDuplicateShells = (primaryRoot: HTMLElement) => {
  const shells = Array.from(document.querySelectorAll(`[${SHELL_MARKER}="true"]`)) as HTMLElement[]
  if (shells.length <= 1) {
    return
  }
  const primaryShell = shells.find((shell) => primaryRoot.contains(shell)) ?? shells[0]
  shells.forEach((shell) => {
    if (shell !== primaryShell) {
      shell.remove()
    }
  })
}

const rootElement = pickRootElement()
if (!rootElement) throw new Error('Failed to find the root element')

rootElement.setAttribute(ROOT_MARKER, 'true')
cleanupDuplicateRoots(rootElement)
cleanupDuplicateShells(rootElement)
applyThemePreference()

const appWindow = window as AppWindow
appWindow.__YIJIANMAI_ROOT_ELEMENT__ = rootElement
installUnauthorizedResponseGuard(appWindow)

appWindow.__YIJIANMAI_BOOT_COUNT__ = (appWindow.__YIJIANMAI_BOOT_COUNT__ ?? 0) + 1
if (isRootDebugEnabled() && appWindow.__YIJIANMAI_BOOT_COUNT__ > 1) {
  console.error('[RootDebug] main.tsx 被重复执行，可能存在多次注入/多次 HMR 评估', new Error().stack)
}

installRootInjectionDebugger(appWindow)

if (!appWindow.__YIJIANMAI_ROOT_OBSERVER__) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (nodeContainsRoot(node)) {
          cleanupDuplicateRoots(rootElement)
          cleanupDuplicateShells(rootElement)
          return
        }
      }
    }
  })
  observer.observe(document.body, { childList: true, subtree: true })
  appWindow.__YIJIANMAI_ROOT_OBSERVER__ = observer
}

const App = () => {
  const [appRouter, setAppRouter] = useState<React.ComponentProps<typeof RouterProvider>['router'] | null>(null)

  useEffect(() => {
    let mounted = true
    import('./router').then((module) => {
      if (mounted) {
        setAppRouter(module.router)
      }
    })
    return () => {
      mounted = false
    }
  }, [])

  if (!appRouter) {
    return null
  }

  return <RouterProvider router={appRouter} />
}

const appRoot = appWindow.__YIJIANMAI_APP_ROOT__ ?? ReactDOM.createRoot(rootElement)
appWindow.__YIJIANMAI_APP_ROOT__ = appRoot

appRoot.render(
  <React.StrictMode>
    <LanguageProvider>
      <DialogProvider>
        <div {...{ [SHELL_MARKER]: 'true' }}>
          <RootErrorBoundary>
            <App />
          </RootErrorBoundary>
        </div>
      </DialogProvider>
    </LanguageProvider>
  </React.StrictMode>
)
